// Shared navigation safety utilities

/**
 * Returns true if the provided route is a safe, same-origin internal path
 * that can be passed to the router/history APIs without causing an open redirect
 * or throwing due to cross-origin navigation.
 */
export function isValidInternalRoute(route: string | null | undefined): boolean {
  if (!route || typeof route !== "string") return false;

  const trimmed = route.trim();
  if (trimmed.length === 0) return false;

  // Must be a root-relative path (internal navigation only)
  if (!trimmed.startsWith("/")) return false;

  // Disallow network/absolute and dangerous schemes
  // e.g., //evil.com, http://..., https://..., javascript:, data:, vbscript:, file:
  if (/^(https?:)?\/\//i.test(trimmed)) return false;
  if (/^(javascript|data|vbscript|file):/i.test(trimmed)) return false;

  // Also check a decoded variant to prevent encoded bypasses (e.g., %2F%2Fevil.com)
  try {
    const decoded = decodeURIComponent(trimmed);
    if (/^(https?:)?\/\//i.test(decoded)) return false;
    if (/^(javascript|data|vbscript|file):/i.test(decoded)) return false;
  } catch {
    // ignore decode errors and continue with other checks
  }

  // Disallow backslashes which can be used in some parsers to escape
  if (trimmed.includes("\\")) return false;

  // Final same-origin check via URL API to ensure history.replaceState won't throw
  try {
    const url = new URL(trimmed, window.location.origin);
    if (url.origin !== window.location.origin) return false;
  } catch {
    return false;
  }

  // Sanity upper bound to avoid absurdly long inputs
  if (trimmed.length > 2048) return false;

  return true;
}


